import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, ShieldCheck } from "lucide-react";

const ICON = { ok: <CheckCircle2 size={16} className="text-brand-green shrink-0" />, warn: <AlertTriangle size={16} className="text-amber-600 shrink-0" />, fail: <XCircle size={16} className="text-brand-maroon shrink-0" /> };
const TONE = { ok: "border-brand-green/30 bg-brand-green/5", warn: "border-amber-400/40 bg-amber-50", fail: "border-brand-maroon/40 bg-red-50" };

export function ReadinessList({ r, compact = false }) {
  if (!r) return null;
  return (
    <div data-testid={`readiness-${r.hotel}`} className="space-y-2">
      <div className={`rounded-lg border px-3 py-2 text-[13px] font-semibold flex items-center gap-2 ${TONE[r.fails ? "fail" : r.warns ? "warn" : "ok"]}`} data-testid={`readiness-verdict-${r.hotel}`}>
        {ICON[r.fails ? "fail" : r.warns ? "warn" : "ok"]} {r.verdict}
      </div>
      <ul className={`grid gap-1.5 ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
        {r.items.map((i) => (
          <li key={i.id} className="flex items-start gap-2 text-[12.5px] rounded-md border border-brand-gold/15 bg-white px-2.5 py-1.5" data-testid={`readiness-item-${r.hotel}-${i.id}`}>
            <span className="mt-0.5">{ICON[i.status]}</span>
            <div className="min-w-0">
              <div className="font-semibold text-brand-ink">{i.label}</div>
              <div className="text-brand-muted">{i.detail}{i.fix ? <span className="text-brand-green"> → {i.fix}</span> : null}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function useReadiness(tenant, hotel) {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!tenant || !hotel) { setR(null); return; }
    let alive = true;
    setLoading(true);
    api.adminReadiness(tenant, hotel).then((d) => alive && setR(d)).catch((e) => alive && toast.error(e.message)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [tenant, hotel]);
  return { r, loading };
}

export default function ProntoDirettore({ hotels }) {
  const [sel, setSel] = useState("");
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const h = hotels.find((x) => `${x.tenant}/${x.hotel}` === sel);
  const check = async () => {
    if (!h) return;
    setBusy(true);
    try { const r = await api.adminReadiness(h.tenant, h.hotel); setResults((m) => ({ ...m, [sel]: r })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-brand-gold/25 p-5 mt-6" data-testid="pronto-direttore">
      <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-brand-gold" /><h3 className="font-serif text-[20px] text-brand-ink">Pronto per il Direttore</h3></div>
      <p className="text-[13px] text-brand-muted mt-1 max-w-3xl">Prima di invitare un Direttore, LEUTELIA verifica che la sua vista sia vuota e pulita: nessun hotel o account di prova nel gruppo, sito letto, analisi, foto e referti pronti. I punti in rosso bloccano l'invito.</p>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <select data-testid="pronto-hotel" value={sel} onChange={(e) => setSel(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-[13.5px] min-w-[260px]">
          <option value="">Scegli un hotel…</option>
          {hotels.map((x) => <option key={`${x.tenant}/${x.hotel}`} value={`${x.tenant}/${x.hotel}`}>{x.name} · {x.tenant}</option>)}
        </select>
        <Button onClick={check} disabled={!h || busy} data-testid="pronto-check" className="h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold">
          {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ShieldCheck size={15} className="mr-2" />} Verifica
        </Button>
      </div>
      {results[sel] && <div className="mt-4"><ReadinessList r={results[sel]} /></div>}
    </div>
  );
}
